// File: frontend/src/services/customTopicEntryService.ts

import { EntryModel } from "../models";
import { ENTRIES_PER_PAGE , API_BASE_URL } from "../constants";
import apiClient from "./apiClient";
import { mapRawEntry as mapEntry } from "./entryMapper";
import { parseServerDate } from "../utils/dateUtils";


export interface CustomTopicEntryResponse {
  entries: EntryModel[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

export const fetchEntriesByCustomTopicId = async (
  customTopicId: number,
  page: number = 1,
  pageSize: number = ENTRIES_PER_PAGE,
  showRookieEntries: boolean = true,
  sort: string = "newest",
  filterToday: boolean = false,
  rookieOnly: boolean = false,
  keyword: string = ""
): Promise<CustomTopicEntryResponse> => {
  const params = new URLSearchParams({
    page:              String(page - 1),
    size:              String(pageSize),
    showRookieEntries: String(showRookieEntries),
    sort,
    filterToday:       String(filterToday),
    rookieOnly:        String(rookieOnly),
  });

  // Keyword is optional and omitted when empty
  if (keyword && keyword.trim()) {
    params.append("keyword", keyword.trim());
  }

  const token = localStorage.getItem("token");
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}/api/entries/customTopicId/${customTopicId}?${params}`, { headers });
  if (!res.ok) throw new Error("Başlık tanımları yüklenemedi.");

  const json = await res.json();
  const entries = (json.content ?? []).map(mapEntry);
  return {
    entries,
    currentPage:  page,
    totalPages:   json.totalPages ?? 1,
    totalItems:   json.totalItems ?? json.totalElements ?? entries.length,
  };
};

export const postCustomTopicEntry = async (content: string, customTopicId: number): Promise<EntryModel> => {
  const raw = await apiClient.post<any>("/api/entries", { content, customTopicId });
  return mapEntry(raw);
};

/** One of the viewer's own entries in a custom topic: permalink id + creation date. */
export interface OwnTopicEntryRef {
  id: number;
  createdAt: Date;
}

/** The viewer's history in one custom topic (see GET .../customTopicId/{id}/mine). */
export interface OwnTopicEntries {
  // The topic's latest visible entry is the viewer's — posting now would be
  // rejected with CUSTOM_TOPIC_CONSECUTIVE_ENTRY
  lastEntryIsMine: boolean;
  // Viewer's earlier entries in the topic, newest first (capped by the backend)
  entries: OwnTopicEntryRef[];
  // All of the viewer's entries in the topic; larger than entries.length when
  // the list was capped
  totalCount: number;
}

/**
 * Fetch the logged-in viewer's own entries in a custom topic and whether the
 * topic's latest entry is theirs. Requires authentication (401 otherwise).
 */
export const fetchOwnCustomTopicEntries = async (customTopicId: number): Promise<OwnTopicEntries> => {
  const raw = await apiClient.get<any>(`/api/entries/customTopicId/${customTopicId}/mine`);
  const entries: OwnTopicEntryRef[] = (raw?.entries ?? []).map((e: any) => ({
    id: e.id,
    createdAt: parseServerDate(e.createdAt),
  }));
  return {
    lastEntryIsMine: !!raw?.lastEntryIsMine,
    entries,
    // Older backends did not send the total; fall back to the list length
    totalCount: typeof raw?.totalCount === "number" ? raw.totalCount : entries.length,
  };
};